import * as React from "react";

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  Updater,
  useReactTable,
  type OnChangeFn,
  type PaginationState as TanstackPaginationState,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { Table } from "./table";
import { PaginationSimple } from "./pagination-simple";
import { useCallback } from "react";
import { PaginationParams } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { RefreshCw } from "lucide-react";

type DataTableColumnMeta = {
  intrinsic?: boolean;
};

type DataTableInfiniteScroll = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};

function getColumnSizingStyle<TData>(column: Column<TData, unknown>): React.CSSProperties | undefined {
  const { size, minSize, maxSize } = column.columnDef;
  const hasCustomMin = minSize !== undefined;
  const hasCustomMax = maxSize !== undefined;
  const hasCustomSize = size !== undefined;

  if (!hasCustomMin && !hasCustomMax && !hasCustomSize) {
    return undefined;
  }

  const style: React.CSSProperties = {};
  if (hasCustomMin) style.minWidth = minSize;
  if (hasCustomMax) style.maxWidth = maxSize;

  // Fixed column: size === minSize === maxSize. Flexible columns omit width so they
  // can share remaining table space (use with `table-fixed` on the <table>).
  if (hasCustomSize && minSize === maxSize) {
    style.width = size;
  } else if (hasCustomSize && !hasCustomMin && !hasCustomMax) {
    style.width = column.getSize();
  }

  return style;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  disabled?: boolean;
  loading?: boolean;
  emptyState?: React.ReactNode;
  pageCount?: number;
  rowCount?: number;
  pagination?: PaginationParams;
  onPaginationChange?: (pagination: PaginationParams) => void;
  infiniteScroll?: DataTableInfiniteScroll;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onRowClick?: (row: Row<TData>) => void;
  scrollable?: boolean;
  className?: string;
}
export const DataTable = <TData, TValue>({
  columns,
  data,
  disabled = false,
  loading = false,
  emptyState = "No results.",
  pageCount,
  rowCount,
  pagination = { page: 1, perPage: 10 },
  onPaginationChange,
  infiniteScroll,
  sorting,
  onSortingChange,
  onRowClick,
  scrollable = false,
  className,
}: DataTableProps<TData, TValue>) => {
  const useInfiniteScroll = Boolean(infiniteScroll);
  const manualPagination = useInfiniteScroll || pageCount !== undefined || rowCount !== undefined;
  const manualSorting = sorting !== undefined;
  disabled = disabled || loading;

  const convertToTanstackPaginationState = (pagination: PaginationParams) => {
    // Tanstack pagination is 0-indexed, so we need to subtract 1 from the current page
    return { pageIndex: pagination.page - 1, pageSize: pagination.perPage };
  };

  const handlePaginationChange = useCallback(
    (updaterOrValue: Updater<TanstackPaginationState>) => {
      if (!onPaginationChange) return;
      let newState: TanstackPaginationState;
      if (typeof updaterOrValue === "function") {
        newState = updaterOrValue(convertToTanstackPaginationState(pagination));
      } else {
        newState = updaterOrValue;
      }
      const newPage = newState.pageIndex + 1;
      const newPerPage = newState.pageSize;
      if (newPage === pagination.page && newPerPage === pagination.perPage) {
        return;
      }
      onPaginationChange({ page: newPage, perPage: newPerPage });
    },
    [onPaginationChange, pagination]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    manualSorting,
    ...(manualSorting ? { enableMultiSort: false as const } : {}),
    pageCount: useInfiniteScroll ? 1 : pageCount,
    rowCount: useInfiniteScroll ? data.length : rowCount,
    state: {
      pagination: useInfiniteScroll
        ? { pageIndex: 0, pageSize: Math.max(data.length, 1) }
        : convertToTanstackPaginationState(pagination),
      sorting,
    },
    onPaginationChange: useInfiniteScroll ? undefined : handlePaginationChange,
    onSortingChange,
  });

  const handleRowClick = (row: Row<TData>, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (disabled || !onRowClick) return;
    const target = e.target as HTMLElement;
    if (target.closest('button, input, a, [role="button"], [role="checkbox"], [data-slot="checkbox"]')) return;
    onRowClick(row);
  };

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const loadMoreRef = React.useRef<HTMLTableSectionElement>(null);

  React.useEffect(() => {
    if (scrollable && !useInfiniteScroll && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [pagination.page, scrollable, useInfiniteScroll]);

  const hasNextPage = infiniteScroll?.hasNextPage ?? false;
  const isFetchingNextPage = infiniteScroll?.isFetchingNextPage ?? false;
  const fetchNextPage = infiniteScroll?.fetchNextPage;

  React.useEffect(() => {
    if (!hasNextPage || !fetchNextPage) return;

    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const root = scrollable ? scrollContainerRef.current : null;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: "200px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollable, data.length]);

  return (
    <div className={cn("flex flex-col gap-2", scrollable && "flex-1 min-h-0 h-full", className)}>
      <div
        ref={scrollContainerRef}
        className={cn(
          "relative rounded-md border border-border",
          scrollable ? "flex-1 min-h-0 overflow-auto" : "overflow-hidden"
        )}
      >
        <div className={disabled ? "pointer-events-none select-none opacity-60" : undefined}>
          <Table wrapperClassName={scrollable ? "overflow-visible" : undefined}>
            <Table.Header>
              {table.getHeaderGroups().map(headerGroup => (
                <Table.Row key={headerGroup.id} className="bg-accent hover:bg-unset">
                  {headerGroup.headers.map(header => {
                    const def = header.column.columnDef as ColumnDef<unknown> & { meta?: DataTableColumnMeta };
                    const intrinsic = def.meta?.intrinsic;
                    return (
                      <Table.Head
                        key={header.id}
                        className={cn(
                          intrinsic ? "w-[1%] whitespace-nowrap" : undefined,
                          scrollable ? "sticky top-0 z-10 bg-accent shadow-xs border-b border-border" : undefined
                        )}
                        style={intrinsic ? undefined : getColumnSizingStyle(header.column)}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </Table.Head>
                    );
                  })}
                </Table.Row>
              ))}
            </Table.Header>
            <Table.Body>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => (
                  <Table.Row
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={onRowClick ? e => handleRowClick(row, e) : undefined}
                    className={onRowClick && !disabled ? "cursor-pointer" : undefined}
                  >
                    {row.getVisibleCells().map(cell => {
                      const def = cell.column.columnDef as ColumnDef<unknown> & { meta?: DataTableColumnMeta };
                      const intrinsic = def.meta?.intrinsic;
                      return (
                        <Table.Cell
                          key={cell.id}
                          className={intrinsic ? "w-[1%] whitespace-nowrap" : undefined}
                          style={intrinsic ? undefined : getColumnSizingStyle(cell.column)}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={columns.length} className="h-24 text-center">
                    {loading ? "Loading..." : emptyState}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
            {useInfiniteScroll && hasNextPage ? (
              <Table.Footer ref={loadMoreRef} className="bg-transparent font-normal">
                <Table.Row className="hover:bg-transparent">
                  <Table.Cell colSpan={columns.length} className="py-3">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <RefreshCw className={cn("size-3.5", isFetchingNextPage && "animate-spin")} />
                      <span className="text-xs font-medium">
                        {isFetchingNextPage ? "Loading more..." : "Scroll to load more"}
                      </span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              </Table.Footer>
            ) : null}
          </Table>
        </div>
      </div>
      {!useInfiniteScroll ? (
        <PaginationSimple
          page={pagination.page}
          totalPages={pageCount ?? 0}
          onPageChange={page => table.setPageIndex(page - 1)}
          disabled={disabled || loading}
        />
      ) : null}
    </div>
  );
};
DataTable.displayName = "DataTable";
